import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

/**
 * Weidner Via6 PetCallout section.
 *
 * A 2x2 square grid that pairs three property photos with a centered
 * pet-policy callout block. The callout occupies the third cell (bottom
 * left on desktop, third slot when stacked on mobile) and contains a
 * heading + CTA, while the other three cells render square photos.
 *
 * Mirrors the "Learn more about our pet policy" block on
 * via6living.com/amenities. Photo tiles default to the property's own
 * `photo_gallery` (indices 0/1/2) — no hardcoded Via6 Seattle CDN URLs.
 * Editors can still swap any tile per-property through the inline editor.
 *
 * Each square image is its own independently-editable slot group
 * (`petcallout.image_1`, `image_2`, `image_4`). The CTA position
 * (cell 3) holds a richtext heading token plus a CTA link slot group.
 * Slot groups and tokens are unrolled (not built via .map) because the
 * schema linter requires every wrapper path to be statically extractable.
 */

const image1Def = {
  label: 'Pet Callout Image #1',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[0].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[0].alt_text',
      defaultValue: 'Pet-friendly community',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image2Def = {
  label: 'Pet Callout Image #2',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Pet-friendly community',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const image4Def = {
  label: 'Pet Callout Image #4',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Pet-friendly community',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const petPolicyCtaDef = {
  label: 'Pet Policy CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/pet-policy',
    },
    label: {
      label: 'Label',
      type: 'text',
      defaultValue: 'Our Pet Policy',
    },
    _target: {
      label: 'Link Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function PetCallout() {
  const img1 = useSlotGroup('petcallout.image_1', image1Def);
  const img2 = useSlotGroup('petcallout.image_2', image2Def);
  const img4 = useSlotGroup('petcallout.image_4', image4Def);
  const cta = useSlotGroup('petcallout.cta_link', petPolicyCtaDef);

  return (
    <section className="bg-[var(--vibe-color-primary)] pb-6 sm:pb-8 md:pb-10">
      <div className="w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[11px] sm:gap-[13px]">
          {/* Cell 1 — square image */}
          <EditableSlotGroup groupPath="petcallout.image_1" definition={image1Def}>
            <div className="relative w-full aspect-square overflow-hidden bg-[var(--vibe-color-primary)]/10">
              {img1.image ? (
                <VibesitesImage
                  url={img1.image}
                  alt={img1.alt || 'Pet-friendly community'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>

          {/* Cell 2 — square image */}
          <EditableSlotGroup groupPath="petcallout.image_2" definition={image2Def}>
            <div className="relative w-full aspect-square overflow-hidden bg-[var(--vibe-color-primary)]/10">
              {img2.image ? (
                <VibesitesImage
                  url={img2.image}
                  alt={img2.alt || 'Pet-friendly community'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>

          {/* Cell 3 — heading + CTA (replaces the third image) */}
          <div className="relative w-full aspect-square flex flex-col items-start justify-center text-left px-6 py-8 sm:px-8 md:px-10 bg-[var(--vibe-color-primary)]">
            <RichTextContent
              path="petcallout.heading"
              as="h2"
              className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-normal text-[var(--vibe-color-text-on-primary)] leading-tight uppercase [&_strong]:font-normal [&_b]:font-normal"
            />

            <EditableSlotGroup groupPath="petcallout.cta_link" definition={petPolicyCtaDef}>
              <a
                href={cta.url || '/pet-policy'}
                target={cta._target || '_self'}
                className="mt-6 inline-block text-center px-8 py-2.5 text-sm font-medium tracking-[0.2em] uppercase rounded-full bg-[var(--vibe-color-secondary)] text-[var(--vibe-color-text-on-primary)] border border-[var(--vibe-color-secondary)] hover:bg-white hover:text-[var(--vibe-color-secondary)] hover:border-white transition-all"
              >
                {cta.label || 'Our Pet Policy'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Cell 4 — square image */}
          <EditableSlotGroup groupPath="petcallout.image_4" definition={image4Def}>
            <div className="relative w-full aspect-square overflow-hidden bg-[var(--vibe-color-primary)]/10">
              {img4.image ? (
                <VibesitesImage
                  url={img4.image}
                  alt={img4.alt || 'Pet-friendly community'}
                  className="w-full h-full object-cover"
                  sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                />
              ) : null}
            </div>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetCallout',
  displayName: 'Pet Callout',
  tokens: {
    'petcallout.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Learn more about our pet policy</p>',
    },
  },
  slotGroups: {
    'petcallout.image_1': image1Def,
    'petcallout.image_2': image2Def,
    'petcallout.image_4': image4Def,
    'petcallout.cta_link': petPolicyCtaDef,
  },
};

(PetCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PetCallout;
